/**
 * iOS Safari / installed-PWA specifics (docs/iphone-acceptance.md). Pure helpers take their
 * environment as arguments so they are unit-testable; the `install*` functions wire them up.
 *
 * - Web Push exists on iOS (16.4+) only for a PWA added to the Home Screen, and the permission
 *   prompt must come from a user gesture.
 * - WebKit revokes a push subscription that receives pushes without showing a notification.
 * - The layout viewport does not shrink for the on-screen keyboard (`interactive-widget` is
 *   ignored), so bottom-pinned UI needs the visual viewport height.
 * - Storage in a browser tab is evicted after 7 days of no use; an installed PWA is exempt, and
 *   `navigator.storage.persist()` asks for it explicitly.
 */

import { type EnvLike, isInstalled, isIOS } from './ios-detect.js'

export { type EnvLike, isInstalled, isIOS }

export type PushGate =
  /** Safari tab on iOS: push is impossible until the app is added to the Home Screen. */
  | 'install-first'
  /** Installed (or non-iOS) and the browser has no push API. */
  | 'unsupported'
  | 'denied'
  /** Ready: the next tap on the button may ask for permission. */
  | 'ready'

export function pushGate(e: EnvLike): PushGate {
  if (isIOS(e) && !isInstalled(e)) return 'install-first'
  if (!e.hasPushManager || !e.hasNotification) return 'unsupported'
  if (e.permission === 'denied') return 'denied'
  return 'ready'
}

export function currentEnv(): EnvLike {
  const nav = navigator as Navigator & { standalone?: boolean }
  return {
    userAgent: nav.userAgent,
    platform: nav.platform,
    maxTouchPoints: nav.maxTouchPoints,
    standalone: nav.standalone === true,
    displayModeStandalone:
      typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches,
    hasPushManager: 'PushManager' in globalThis,
    hasNotification: 'Notification' in globalThis,
    ...('Notification' in globalThis ? { permission: Notification.permission } : {}),
  }
}

export const pushGateNow = () => pushGate(currentEnv())

export const GATE_COPY: Record<Exclude<PushGate, 'ready'>, string> = {
  'install-first':
    'On iPhone, push works only from the Home Screen app. In Safari tap Share, then Add to Home Screen, open Viko from its icon, and come back here.',
  unsupported: 'This browser has no push support.',
  denied:
    'Notifications are blocked for Viko. Open iOS Settings, Notifications, Viko (or your browser’s site settings) and allow them.',
}

/** Ask the browser to keep this origin's IndexedDB (vault, drafts, outbox). Best effort. */
export async function requestPersistentStorage(
  s: Pick<StorageManager, 'persist' | 'persisted'> | undefined = navigator.storage,
): Promise<boolean> {
  try {
    if (!s?.persist) return false
    return (await s.persisted?.()) || (await s.persist())
  } catch {
    return false
  }
}

/**
 * Mirrors the visual viewport into `--vk-vvh` on iOS only, so `.app` shrinks above the keyboard and
 * the bark box / composer stay visible. Other platforms keep `100dvh`.
 */
export function installVisualViewport(
  root: HTMLElement = document.documentElement,
  env: EnvLike = currentEnv(),
  vv: VisualViewport | null = globalThis.visualViewport ?? null,
): () => void {
  if (!vv || !isIOS(env)) return () => {}
  const sync = () => {
    root.style.setProperty('--vk-vvh', `${Math.round(vv.height)}px`)
    // iOS scrolls the (non-scrollable) page to reveal the focused field; keep the shell pinned.
    if (vv.offsetTop) globalThis.scrollTo(0, 0)
  }
  sync()
  vv.addEventListener('resize', sync)
  vv.addEventListener('scroll', sync)
  return () => {
    vv.removeEventListener('resize', sync)
    vv.removeEventListener('scroll', sync)
    root.style.removeProperty('--vk-vvh')
  }
}

/** Installed iOS PWAs are rarely cold-launched: check for a new service worker on every resume. */
export function updateOnResume(reg: Pick<ServiceWorkerRegistration, 'update'>): () => void {
  const h = () => {
    if (document.visibilityState === 'visible') void reg.update().catch(() => {})
  }
  document.addEventListener('visibilitychange', h)
  return () => document.removeEventListener('visibilitychange', h)
}

/**
 * The Web Speech API exists in iOS Safari but fails in Home Screen apps (no permission prompt,
 * `service-not-allowed`). There the keyboard's own dictation mic is the reliable path.
 */
export function inAppDictationUsable(e: EnvLike): boolean {
  return !(isIOS(e) && isInstalled(e))
}
